import { ScoreCriteriaType } from "../../../schema/score.schema";
import { CellAssignment, PersonaView, SegmentView } from "../../../shell/scoreConfigView";

/**
 * The numbers the overview's summary box states (PREC-4055), computed over the
 * effective (unsaved edits included) matrix so the box moves with the table.
 */

export interface ScoringUsage {
	criteriaId: number;
	name: string;
	locked: boolean;
	cells: number;
}

export interface OverviewStats {
	segments: number;
	personas: number;
	/** Linked, active pairs — what the matrix can grade. */
	live: number;
	/** Active pairs where the persona does not exist in the segment. */
	off: number;
	scored: number;
	missing: number;
	usage: ScoringUsage[];
}

export const computeOverviewStats = (
	segments: SegmentView[],
	personas: PersonaView[],
	criteria: ScoreCriteriaType[],
	isLinked: (segment: SegmentView, persona: PersonaView) => boolean,
	assignmentOf: (segment: SegmentView, persona: PersonaView) => CellAssignment | null
): OverviewStats => {
	const liveSegments = segments.filter((segment) => segment.active);
	const livePersonas = personas.filter((persona) => persona.active);
	const perScoring = new Map<number, number>(criteria.map((scoring) => [scoring.id, 0]));

	let live = 0;
	let off = 0;
	let scored = 0;

	for (const segment of liveSegments) {
		for (const persona of livePersonas) {
			if (!isLinked(segment, persona)) {
				off += 1;
				continue;
			}

			live += 1;

			const assignment = assignmentOf(segment, persona);

			if (assignment) {
				scored += 1;
				perScoring.set(assignment.criteriaId, (perScoring.get(assignment.criteriaId) ?? 0) + 1);
			}
		}
	}

	return {
		segments: liveSegments.length,
		personas: livePersonas.length,
		live,
		off,
		scored,
		missing: live - scored,
		usage: criteria.map((scoring) => ({
			criteriaId: scoring.id,
			name: scoring.name,
			locked: scoring.locked,
			cells: perScoring.get(scoring.id) ?? 0,
		})),
	};
};
